<template>
  <div>
    <MainHeader title="Privacy Policy">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view legal-doc-view">
      <div class="wrapper-card legal-doc-card">
        <div class="legal-doc-header">
          <h1 class="legal-doc-title">Lucky Friday Privacy Policy</h1>
          <div class="legal-doc-dates">
            <span class="legal-date-badge">Effective: 04/09/2025</span>
            <span class="legal-date-badge">Updated: 04/09/2025</span>
          </div>
        </div>

        <p class="legal-body">
          Lucky Friday ("we," "us," or "our") respects your privacy and is
          committed to protecting the personal and financial data you provide to
          us. This Privacy Policy explains how we collect, use, share, and
          protect your information when you use our web application and services
          ("Service"). By using the Service, you agree to the practices
          described in this Privacy Policy.
        </p>

        <div class="legal-section">
          <h3 class="legal-section-title">1. Information We Collect</h3>
          <p class="legal-body"><strong>a. Information You Provide</strong></p>
          <ul class="legal-list">
            <li>
              Name, email address, and account login credentials (via Auth0)
            </li>
            <li>
              Budget details, financial categories, notes, and custom labels
            </li>
          </ul>
          <p class="legal-body">
            <strong>b. Information from Connected Financial Accounts</strong>
          </p>
          <ul class="legal-list">
            <li>Transaction data (amounts, dates, descriptions)</li>
            <li>Account balances and types</li>
            <li>
              Account identifiers (last 2–4 digits only; no full credentials)
            </li>
          </ul>
          <p class="legal-body">
            We use Plaid Inc. to securely access financial data. Plaid's privacy
            policy is available at
            <a href="https://plaid.com/legal" target="_blank" class="legal-link"
              >plaid.com/legal</a
            >. You may revoke Plaid access at any time from within the app or
            your financial institution.
          </p>
          <p class="legal-body">
            <strong>c. Automatically Collected Information</strong>
          </p>
          <ul class="legal-list">
            <li>IP address, browser type, and device information</li>
            <li>Usage data, including page views and actions taken</li>
            <li>Error logs and performance data</li>
          </ul>
          <p class="legal-body">
            We use cookies and similar technologies for session management,
            analytics, and security.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">2. How We Use Your Information</h3>
          <ul class="legal-list">
            <li>Provide and maintain the Service</li>
            <li>Securely connect to financial data via Plaid</li>
            <li>Authenticate your identity via Auth0</li>
            <li>Customize your budgeting experience</li>
            <li>Monitor usage and improve performance</li>
            <li>Respond to inquiries and provide support</li>
            <li>Meet legal or regulatory obligations</li>
          </ul>
          <p class="legal-body">
            <strong
              >We do not sell or rent your personal data to third
              parties.</strong
            >
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">3. How We Share Your Information</h3>
          <p class="legal-body">
            We share your data only with trusted service providers that support
            our operations:
          </p>
          <ul class="legal-list">
            <li><strong>Plaid</strong> – Secure financial data access</li>
            <li>
              <strong>Auth0</strong> – Authentication and identity management
            </li>
            <li>
              <strong>Microsoft Azure</strong> – Secure hosting, encryption, and
              database storage
            </li>
          </ul>
          <p class="legal-body">
            Each third party is contractually obligated to safeguard your data
            and comply with applicable privacy laws.
          </p>
          <p class="legal-body">We may also disclose information:</p>
          <ul class="legal-list">
            <li>To comply with legal obligations (e.g., court orders)</li>
            <li>To prevent fraud or abuse</li>
            <li>
              As part of a merger, sale, or acquisition (with notice to you)
            </li>
          </ul>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">4. Data Storage and Security</h3>
          <ul class="legal-list">
            <li>All data is encrypted in transit using TLS 1.2+</li>
            <li>
              Data at rest is protected using AES-256 via Azure-managed
              encryption
            </li>
          </ul>
          <p class="legal-body">
            Access to user data is restricted to authorized systems and
            personnel. We maintain audit logs and regularly review security
            practices.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">5. Data Retention</h3>
          <p class="legal-body">
            We retain user data for as long as your account is active or as
            necessary to provide the Service. You may request deletion of your
            data at any time via your account settings or by emailing us. Some
            anonymized data may be retained for analytics and legal compliance.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">6. Your Rights</h3>
          <p class="legal-body">
            Depending on your location, you may have the right to:
          </p>
          <ul class="legal-list">
            <li>Access a copy of your data</li>
            <li>Request correction or deletion</li>
            <li>Revoke consent to data processing</li>
            <li>Opt out of analytics or cookies</li>
          </ul>
          <p class="legal-body">
            To exercise your rights, email us at
            <a href="mailto:support@luckyfridayapp.com" class="legal-link"
              >support@luckyfridayapp.com</a
            >. We may ask you to verify your identity before fulfilling
            requests.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">7. Children's Privacy</h3>
          <p class="legal-body">
            The Service is not intended for children under 13 (or under 16 in
            some jurisdictions). We do not knowingly collect personal
            information from minors.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">8. International Users</h3>
          <p class="legal-body">
            If you are accessing the Service from outside the United States,
            your data will be transferred to and processed in the U.S. By using
            the Service, you consent to this transfer.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">9. Changes to This Policy</h3>
          <p class="legal-body">
            We may update this Privacy Policy from time to time. We will notify
            you of significant changes by email or in-app notice. Continued use
            of the Service means you accept the updated policy.
          </p>
        </div>

        <div class="legal-section">
          <h3 class="legal-section-title">10. Contact Us</h3>
          <p class="legal-body">
            If you have questions about this Privacy Policy or your personal
            data, please contact us at:
          </p>
          <a
            href="mailto:support@luckyfridayapp.com"
            class="legal-contact-link"
          >
            <font-awesome-icon
              :icon="['fas', 'envelope']"
              style="margin-right: 6px"
            />
            support@luckyfridayapp.com
          </a>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'PrivacyPolicy',
  components: { MainHeader },
  setup() {
    const router = useRouter();
    return { router };
  },
};
</script>

<style scoped>
.legal-doc-view {
  max-width: 600px;
  margin: 0 auto;
}
.legal-doc-card {
  animation: cardSlideUp 0.4s cubic-bezier(0.34, 1.2, 0.64, 1) both;
  padding: 1.5rem;
}
.legal-doc-header {
  margin-bottom: 1.25rem;
  padding-bottom: 1rem;
  border-bottom: 1.5px solid #eeedfe;
}
.dark-theme .legal-doc-header {
  border-bottom-color: #2a3a4e;
}
.legal-doc-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #004751;
  margin: 0 0 0.6rem;
  letter-spacing: -0.01em;
  line-height: 1.3;
}
.dark-theme .legal-doc-title {
  color: white;
}
.legal-doc-dates {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.legal-date-badge {
  font-size: 0.7rem;
  font-weight: 600;
  background: var(--purple-50);
  color: var(--color-accent-purple);
  padding: 2px 10px;
  border-radius: 20px;
}
.dark-theme .legal-date-badge {
  background: #1e1a40;
}
.legal-section {
  margin-bottom: 1.25rem;
  padding-bottom: 1.25rem;
  border-bottom: 1px solid #f0f0f0;
}
.dark-theme .legal-section {
  border-bottom-color: #2a3a4e;
}
.legal-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}
.legal-section-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: var(--color-accent-purple);
  margin: 0 0 0.6rem;
}
.dark-theme .legal-section-title {
  color: var(--color-accent-teal);
}
.legal-body {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  margin: 0 0 0.6rem;
}
.dark-theme .legal-body {
  color: #d4d2d2;
}
.legal-list {
  font-size: 0.82rem;
  color: #515a65;
  line-height: 1.65;
  padding-left: 1.25rem;
  margin: 0.4rem 0;
}
.dark-theme .legal-list {
  color: #d4d2d2;
}
.legal-list li {
  margin-bottom: 0.5rem;
}
.legal-list li:last-child {
  margin-bottom: 0;
}
.legal-link {
  color: var(--color-accent-purple);
  font-weight: 500;
  text-decoration: none;
}
.legal-link:hover {
  text-decoration: underline;
}
.dark-theme .legal-link {
  color: var(--color-accent-teal);
}
.legal-contact-link {
  display: inline-flex;
  align-items: center;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--color-accent-purple);
  text-decoration: none;
  background: var(--purple-50);
  padding: 0.5rem 1rem;
  border-radius: var(--radius-pill);
  transition: background var(--transition-fast);
}
.legal-contact-link:hover {
  background: var(--purple-100);
}
.dark-theme .legal-contact-link {
  background: #1e1a40;
  color: var(--color-accent-teal);
}
</style>

<!-- <template>
  <div>
    <MainHeader title="Privacy Policy">
      <template #left>
        <font-awesome-icon
          :icon="['fas', 'arrow-left-long']"
          class="pointer"
          @click="router.push({ name: 'Legal' })"
        />
      </template>
    </MainHeader>

    <div class="view support-page">
      <div class="wrapper-card closing-text ps-2 pe-2 small-grey-text">
        <div>
          <h3>Lucky Friday Privacy Policy</h3>
          <h4>Effective Date: 04/09/2025</h4>
          <h4>Last Updated: 04/09/2025</h4>

          <p>
            Lucky Friday (“we,” “us,” or “our”) respects your privacy and is
            committed to protecting the personal and financial data you provide
            to us. This Privacy Policy explains how we collect, use, share, and
            protect your information when you use our web application and
            services (“Service”). By using the Service, you agree to the
            practices described in this Privacy Policy.
          </p>

          <h4>1. Information We Collect</h4>
          <ul>
            <li>
              <strong>a. Information You Provide</strong>
              <ul>
                <li>
                  Name, email address, and account login credentials (via Auth0)
                </li>
                <li>
                  Budget details, financial categories, notes, and custom labels
                </li>
              </ul>
            </li>
            <li>
              <strong>b. Information from Connected Financial Accounts</strong>
              <ul>
                <li>Transaction data (amounts, dates, descriptions)</li>
                <li>Account balances and types</li>
                <li>
                  Account identifiers (last 2–4 digits only; no full
                  credentials)
                </li>
              </ul>
              <p>
                We use Plaid Inc. to securely access financial data. Plaid’s
                privacy policy is available at
                <a href="https://plaid.com/legal" target="_blank"
                  >plaid.com/legal</a
                >. You may revoke Plaid access at any time from within the app
                or your financial institution.
              </p>
            </li>
            <li>
              <strong>c. Automatically Collected Information</strong>
              <ul>
                <li>IP address, browser type, and device information</li>
                <li>Usage data, including page views and actions taken</li>
                <li>Error logs and performance data</li>
              </ul>
              <p>
                We use cookies and similar technologies for session management,
                analytics, and security.
              </p>
            </li>
          </ul>

          <h4>2. How We Use Your Information</h4>
          <ul>
            <li>Provide and maintain the Service</li>
            <li>Securely connect to financial data via Plaid</li>
            <li>Authenticate your identity via Auth0</li>
            <li>Customize your budgeting experience</li>
            <li>Monitor usage and improve performance</li>
            <li>Respond to inquiries and provide support</li>
            <li>Meet legal or regulatory obligations</li>
          </ul>
          <p>
            <strong
              >We do not sell or rent your personal data to third
              parties.</strong
            >
          </p>

          <h4>3. How We Share Your Information</h4>
          <p>
            We share your data only with trusted service providers that support
            our operations:
          </p>
          <ul>
            <li><strong>Plaid</strong> – Secure financial data access</li>
            <li>
              <strong>Auth0</strong> – Authentication and identity management
            </li>
            <li>
              <strong>Microsoft Azure</strong> – Secure hosting, encryption, and
              database storage
            </li>
          </ul>
          <p>
            Each third party is contractually obligated to safeguard your data
            and comply with applicable privacy laws.
          </p>
          <p>We may also disclose information:</p>
          <ul>
            <li>To comply with legal obligations (e.g., court orders)</li>
            <li>To prevent fraud or abuse</li>
            <li>
              As part of a merger, sale, or acquisition (with notice to you)
            </li>
          </ul>

          <h4>4. Data Storage and Security</h4>
          <ul>
            <li>All data is encrypted in transit using TLS 1.2+</li>
            <li>
              Data at rest is protected using AES-256 via Azure-managed
              encryption
            </li>
          </ul>
          <p>
            Access to user data is restricted to authorized systems and
            personnel. We maintain audit logs and regularly review security
            practices.
          </p>

          <h4>5. Data Retention</h4>
          <p>
            We retain user data for as long as your account is active or as
            necessary to provide the Service. You may request deletion of your
            data at any time via your account settings or by emailing us. Some
            anonymized data may be retained for analytics and legal compliance.
          </p>

          <h4>6. Your Rights</h4>
          <p>Depending on your location, you may have the right to:</p>
          <ul>
            <li>Access a copy of your data</li>
            <li>Request correction or deletion</li>
            <li>Revoke consent to data processing</li>
            <li>Opt out of analytics or cookies</li>
          </ul>
          <p>
            To exercise your rights, email us at
            <a href="mailto:support@luckyfridayapp.com"
              >support@luckyfridayapp.com</a
            >. We may ask you to verify your identity before fulfilling
            requests.
          </p>

          <h4>7. Children’s Privacy</h4>
          <p>
            The Service is not intended for children under 13 (or under 16 in
            some jurisdictions). We do not knowingly collect personal
            information from minors.
          </p>

          <h4>8. International Users</h4>
          <p>
            If you are accessing the Service from outside the United States,
            your data will be transferred to and processed in the U.S. By using
            the Service, you consent to this transfer.
          </p>

          <h4>9. Changes to This Policy</h4>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of significant changes by email or in-app notice. Continued use
            of the Service means you accept the updated policy.
          </p>

          <h4>10. Contact Us</h4>
          <p>
            If you have questions about this Privacy Policy or your personal
            data, please contact us at:
          </p>
          <p>
            📧
            <a href="mailto:support@luckyfridayapp.com"
              >support@luckyfridayapp.com</a
            >
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MainHeader from '../../../components/View-Header.vue';
import { useRouter } from 'vue-router';

export default {
  name: 'User-Support',
  components: {
    MainHeader,
  },
  setup() {
    const router = useRouter();

    return {
      router,
    };
  },
};
</script>
<style scoped>
a {
  color: black;
}
</style> -->
